import React, { useEffect, useMemo, useRef, useState } from "react";
import { Formik } from "formik";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "components/loader";
import ImageDropzone from "components/input/ImageDropzone";
import TitleDescriptionListInput from "components/input/TitleDescriptionListInput";
import ToolListInput from "components/input/ToolListInput";
import AnimateButton from "components/animateButton";
import { validateFile } from "modules/userManagement/action";
import { GET_READ_SIGNED_URLS, UPLOAD_FILE_TO_S3 } from "utils/services";
import { FOLDER, FOLDER_TYPE } from "utils/constants";
import { buildServiceUpdatePayload, mapServiceDetail } from "utils/apiMappers";
import { ServiceDetailSchema } from "utils/schema";
import {
  APIResponseModal,
  ServiceTitleDescriptionItem,
  ServiceToolItem,
} from "utils/modals";
import { getServiceDetail, updateService } from "./action";
import { styles } from "./styles";
import ROUTE_NAMES from "routes/routeNames";
import {
  SERVICE_CATEGORY,
  SERVICE_CATEGORY_LABEL,
  SERVICE_CATEGORY_SLUG,
  toServiceCategory,
} from "utils/serviceCategory";
import { displayOrNA, isLocalPreviewUrl } from "utils/commonFunctions";

type SectionProps = {
  index: number;
  title: string;
  hint: string;
  count?: number;
  countLabel?: string;
  children: React.ReactNode;
};

type ServiceDetailFormData = {
  _id: string;
  category: number;
  typeId: string;
  serviceName: string;
  serviceTime: string;
  image: string;
  commonQuestion: any[];
  howItWorks: ServiceTitleDescriptionItem[];
  requirementFromUser: ServiceToolItem[];
  tools: ServiceToolItem[];
  benefits: ServiceTitleDescriptionItem[];
};

const SERVICE_TIMES = [
  "30 minutes",
  "45 minutes",
  "60 minutes",
  "60-90 min",
  "90 minutes",
  "2 Hours",
  "120 mins",
];

const CATEGORY_OPTIONS = [
  SERVICE_CATEGORY.CAR,
  SERVICE_CATEGORY.BIKE,
  SERVICE_CATEGORY.SOFA,
  SERVICE_CATEGORY.TANK,
  SERVICE_CATEGORY.CARPET,
];

const CATEGORY_TYPE_HINT: { [key in SERVICE_CATEGORY]: string } = {
  [SERVICE_CATEGORY.CAR]: "Update the car service details.",
  [SERVICE_CATEGORY.BIKE]: "Update the bike service details.",
  [SERVICE_CATEGORY.SOFA]: "Update the sofa service details.",
  [SERVICE_CATEGORY.TANK]: "Update the tank service details.",
  [SERVICE_CATEGORY.CARPET]: "Update the carpet service details.",
};

const getItemFile = (item?: ServiceToolItem, fallback?: File) => {
  if (item?.file instanceof File) return item.file;
  if (!item?.image && !item?.previewUrl) return undefined;
  return fallback instanceof File ? fallback : undefined;
};

const mergeMediaFiles = (
  items: ServiceToolItem[] = [],
  files: (File | undefined)[] = []
) =>
  items.map((item, index) => {
    const file = getItemFile(item, files[index]);
    return file ? { ...item, file } : { ...item, file: undefined };
  });

const hasPendingMediaFile = (
  items: ServiceToolItem[] = [],
  files: (File | undefined)[] = []
) => mergeMediaFiles(items, files).some((item) => item.file instanceof File);

const rememberMediaFiles = (
  items: ServiceToolItem[] = [],
  previous: (File | undefined)[] = []
) => items.map((item, index) => getItemFile(item, previous[index]));

const hasAwsSignedQuery = (url: string) =>
  /[?&]X-Amz-(Signature|Algorithm)=/i.test(url);

/**
 * Cover was the only field going through signed-URL resolution; tools/requirements
 * use the API URL directly and render fine. Public https URLs should do the same.
 * Only bare S3 keys need a signed read URL.
 */
const resolveCoverPreview = async (fileUrl: string) => {
  if (!fileUrl || isLocalPreviewUrl(fileUrl)) return fileUrl;
  if (hasAwsSignedQuery(fileUrl)) return fileUrl;
  if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
  try {
    const signedUrls = await GET_READ_SIGNED_URLS([fileUrl]);
    return signedUrls?.[0] || fileUrl;
  } catch {
    return fileUrl;
  }
};

function FormSection({
  index,
  title,
  hint,
  count,
  countLabel,
  children,
}: SectionProps) {
  return (
    <Paper elevation={0} sx={styles.section}>
      <Box sx={styles.sectionHeader}>
        <Box sx={styles.sectionHeaderLeft}>
          <Box sx={styles.sectionIndex}>{index}</Box>
          <Box>
            <Typography sx={styles.sectionTitle}>{title}</Typography>
            <Typography sx={styles.sectionHint} variant="body2">
              {hint}
            </Typography>
          </Box>
        </Box>
        {typeof count === "number" ? (
          <Box sx={styles.sectionCount}>
            {count} {countLabel || (count === 1 ? "item" : "items")}
          </Box>
        ) : null}
      </Box>
      {children}
    </Paper>
  );
}

const ServiceDetailsPage = () => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const serviceId = searchParams.get("serviceId") || "";
  const openInEditMode = searchParams.get("edit") === "true";
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [initialValues, setInitialValues] =
    useState<ServiceDetailFormData | null>(null);
  const [isEditMode, setIsEditMode] = useState(openInEditMode);
  const fileRef = useRef<HTMLInputElement>(null);
  const titleImageKeyRef = useRef("");
  const titleFileRef = useRef<File | null>(null);
  const loadedCoverPreviewRef = useRef("");
  const toolsFilesRef = useRef<(File | undefined)[]>([]);
  const requirementFilesRef = useRef<(File | undefined)[]>([]);

  const resolvedCategory = useMemo(
    () => toServiceCategory(initialValues?.category),
    [initialValues?.category]
  );

  const goToListing = (category = resolvedCategory) => {
    const path = `${ROUTE_NAMES.MAIN_ROUTES.SERVICE_MANAGEMENT}/${
      SERVICE_CATEGORY_SLUG[toServiceCategory(category)]
    }`;
    navigate(path);
  };

  const exitEditMode = () => {
    setIsEditMode(false);
    if (!openInEditMode) return;
    const nextParams = new URLSearchParams(location.search);
    nextParams.delete("edit");
    const query = nextParams.toString();
    navigate(
      `${ROUTE_NAMES.MAIN_ROUTES.SERVICE_DETAIL}${query ? `?${query}` : ""}`,
      { replace: true }
    );
  };

  const uploadImageFile = (
    file: File,
    callback: (imageKey: string, imageUrl: string) => void
  ) => {
    validateFile(file, 2080)
      .then(() => {
        const localPreview = URL.createObjectURL(file);
        callback("", localPreview);
      })
      .catch(() => {});
  };

  const onUploadImageClick = () => {
    if (!isEditMode) return;
    fileRef.current?.click();
  };

  const onImagePicked = (
    evt: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: Function
  ) => {
    const file = evt.target.files?.[0];
    if (!file || !isEditMode) return;
    validateFile(file, 2080)
      .then(() => {
        const localPreview = URL.createObjectURL(file);
        if (isLocalPreviewUrl(imageUrl)) URL.revokeObjectURL(imageUrl);
        titleFileRef.current = file;
        titleImageKeyRef.current = "";
        setImageUrl(localPreview);
        setFieldValue("image", "");
              })
      .catch(() => {});
    evt.target.value = "";
  };

  const uploadMediaList = async (
    items: ServiceToolItem[] = [],
    folder: FOLDER_TYPE
  ) => {
    return Promise.all(
      items.map(async (item: ServiceToolItem) => {
        const file = item?.file instanceof File ? item.file : undefined;
        if (file) {
          const fileUrl = await UPLOAD_FILE_TO_S3(file, folder);
          return { ...item, image: fileUrl, file: undefined, previewUrl: undefined };
        }
        return { ...item, file: undefined, previewUrl: undefined };
      })
    );
  };

  const buildInitialValues = async (data: any) => {
    const mapped = mapServiceDetail(data);
    if (!mapped) {
      setInitialValues(null);
      return;
    }

    const image = mapped.image ?? "";
    const previewSource =
      mapped.imagePreviewSource || data?.image || data?.data?.image || image;
    const displayUrl = await resolveCoverPreview(previewSource);

    titleImageKeyRef.current = image;
    titleFileRef.current = null;
    toolsFilesRef.current = [];
    requirementFilesRef.current = [];
    loadedCoverPreviewRef.current = displayUrl;
    setImageUrl((current) => {
      if (isLocalPreviewUrl(current) && current !== displayUrl) {
        URL.revokeObjectURL(current);
      }
      return displayUrl;
    });
    setInitialValues({
      _id: mapped._id ?? "",
      category: toServiceCategory(mapped.category),
      typeId: mapped.typeId ?? "",
      serviceName: mapped.serviceName ?? "",
      serviceTime: mapped.serviceTime ?? "",
      image,
      commonQuestion: mapped.commonQuestion ?? [],
      howItWorks: mapped.howItWorks ?? [],
      benefits: mapped.benefits ?? [],
      tools: mapped.tools ?? [],
      requirementFromUser: mapped.requirementFromUser ?? [],
    });
  };

  const loadServiceDetail = async () => {
    if (!serviceId) {
      return;
    }

    setLoading(true);
    dispatch(
      getServiceDetail(serviceId, async (response: any) => {
        if (response?.isSuccess && response?.data) {
          await buildInitialValues(response.data);
        } else {
          setInitialValues(null);
        }
        setLoading(false);
      })
    );
  };

  useEffect(() => {
    if (openInEditMode) {
      setIsEditMode(true);
    }
  }, [openInEditMode, serviceId]);

  useEffect(() => {
    loadServiceDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, serviceId]);

  if (loading && !initialValues) {
    return <Loader />;
  }

  if (!initialValues) {
    return (
      <Box sx={styles.page}>
        <Box sx={styles.header}>
          <IconButton
            sx={styles.backBtn}
            onClick={() => goToListing()}
            aria-label="Back to service list"
          >
            <ArrowBackRoundedIcon fontSize="small" />
          </IconButton>
          <Box>
            <Typography sx={styles.heading}>Service Detail</Typography>
            <Typography sx={styles.subtitle}>
              Service detail could not be loaded for the selected record.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ ...styles.emptyState, width: "100%" }}>
          <Typography sx={styles.emptyStateTitle}>
            No service detail found
          </Typography>
          <Typography sx={styles.emptyStateHint}>
            Return to the service listing and try opening the record again.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.page}>
      <Formik<ServiceDetailFormData>
        enableReinitialize
        initialValues={initialValues}
        validationSchema={ServiceDetailSchema()}
        onSubmit={async (values) => {
          if (!isEditMode) return;
          setLoading(true);
          try {
            let image = values.image;
            if (titleFileRef.current) {
              image = await UPLOAD_FILE_TO_S3(titleFileRef.current, FOLDER.SERVICE);
            }

            const toolsWithFiles = mergeMediaFiles(values.tools, toolsFilesRef.current);
            const requirementsWithFiles = mergeMediaFiles(
              values.requirementFromUser,
              requirementFilesRef.current
            );
            const tools = await uploadMediaList(toolsWithFiles, FOLDER.SERVICE_TOOLS);
            const requirementFromUser = await uploadMediaList(
              requirementsWithFiles,
              FOLDER.SERVICE_REQUIREMENTS
            );

            const submittedValues = {
              ...values,
              image,
              tools,
              requirementFromUser,
            };
            const payload = buildServiceUpdatePayload(
              initialValues,
              submittedValues
            );
            if (!Object.keys(payload).length) {
              setLoading(false);
              exitEditMode();
              return;
            }

            dispatch(
              updateService(
                initialValues._id || serviceId,
                payload,
                async (response: APIResponseModal) => {
                  if (response.isSuccess) {
                    titleFileRef.current = null;
                    titleImageKeyRef.current = image;
                    toolsFilesRef.current = [];
                    requirementFilesRef.current = [];
                    const detail = response.data || {};
                    await buildInitialValues({
                      ...submittedValues,
                      ...detail,
                      image: detail.image || image,
                      howItWorks: Array.isArray(detail.howItWorks)
                        ? detail.howItWorks
                        : submittedValues.howItWorks,
                      benefits: Array.isArray(detail.benefits)
                        ? detail.benefits
                        : submittedValues.benefits,
                      tools: Array.isArray(detail.tools)
                        ? detail.tools
                        : tools,
                      requirementFromUser: Array.isArray(
                        detail.requirementFromUser
                      )
                        ? detail.requirementFromUser
                        : requirementFromUser,
                    });
                    exitEditMode();
                  }
                  setLoading(false);
                }
              )
            );
          } catch (err: any) {
            setLoading(false);
          }
        }}
      >
        {({
          errors,
          handleBlur,
          handleSubmit,
          touched,
          values,
          setFieldValue,
          dirty,
          resetForm,
        }) => {
          const category = toServiceCategory(values.category);
          const howItWorksCount = values.howItWorks?.length ?? 0;
          const benefitsCount = values.benefits?.length ?? 0;
          const toolsCount = values.tools?.length ?? 0;
          const requirementsCount = values.requirementFromUser?.length ?? 0;
          const hasPendingMedia =
            Boolean(titleFileRef.current) ||
            hasPendingMediaFile(values.tools, toolsFilesRef.current) ||
            hasPendingMediaFile(
              values.requirementFromUser,
              requirementFilesRef.current
            );
          const serviceTimeValue = isEditMode
            ? values.serviceTime
            : displayOrNA(values.serviceTime);
          const serviceTimeOptions = Array.from(
            new Set([serviceTimeValue, ...SERVICE_TIMES].filter(Boolean))
          );

          return (
            <form noValidate onSubmit={handleSubmit}>
              <FormSection
                index={1}
                title="Basic details"
                hint={CATEGORY_TYPE_HINT[category]}
              >
                <Box sx={styles.basicsLayout}>
                  <FormControl
                    fullWidth
                    error={Boolean(touched.image && errors.image)}
                  >
                    <Typography sx={styles.fieldLabel}>Cover image</Typography>
                    <Box sx={styles.coverPanel}>
                      <Box sx={styles.coverDropzone} onClick={onUploadImageClick}>
                        <ImageDropzone
                          variant="cover"
                          imageUrl={imageUrl}
                          title="Upload cover"
                          hint="JPG or PNG"
                          onClick={onUploadImageClick}
                        />
                      </Box>
                    </Box>
                    <Typography sx={styles.coverMobileHint}>
                      Image will show the same in the mobile application
                    </Typography>
                    <Typography sx={styles.coverCaption}>
                      Preview size 163 × 229 pixels.
                    </Typography>
                    <input
                      id="serviceDetailFileInput"
                      type="file"
                      ref={fileRef}
                      accept=".jpg,.jpeg,.png"
                      style={{ display: "none" }}
                      disabled={!isEditMode}
                      onChange={(e) => onImagePicked(e, setFieldValue)}
                    />
                    {touched.image && errors.image && (
                      <FormHelperText error>{String(errors.image)}</FormHelperText>
                    )}
                  </FormControl>

                  <Box sx={styles.basicsFields}>
                    <Box sx={styles.basicsRow}>
                      <FormControl
                        fullWidth
                        error={Boolean(touched.category && errors.category)}
                      >
                        <InputLabel>Category</InputLabel>
                        <Select
                          sx={styles.selectDropdown}
                          name="category"
                          variant="outlined"
                          label="Category"
                          value={values.category}
                          disabled={!isEditMode}
                          onChange={(e: any) => {
                            const nextCategory = toServiceCategory(e.target.value);
                            setFieldValue("category", nextCategory);
                          }}
                        >
                          {CATEGORY_OPTIONS.map((item) => (
                            <MenuItem key={item} value={item}>
                              {SERVICE_CATEGORY_LABEL[item]}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl
                        fullWidth
                        error={Boolean(touched.serviceTime && errors.serviceTime)}
                      >
                        <InputLabel>Service Time</InputLabel>
                        <Select
                          sx={styles.selectDropdown}
                          name="serviceTime"
                          variant="outlined"
                          label="Service Time"
                          value={serviceTimeValue}
                          disabled={!isEditMode}
                          onChange={(e: any) => {
                            setFieldValue("serviceTime", e.target.value);
                          }}
                        >
                          {serviceTimeOptions.map((item) => (
                            <MenuItem key={item} value={item}>
                              {item}
                            </MenuItem>
                          ))}
                        </Select>
                        {touched.serviceTime && errors.serviceTime && (
                          <FormHelperText error>{errors.serviceTime}</FormHelperText>
                        )}
                      </FormControl>
                    </Box>

                    <FormControl
                      fullWidth
                      error={Boolean(touched.serviceName && errors.serviceName)}
                    >
                      <InputLabel htmlFor="service-detail-name">
                        Service Name
                      </InputLabel>
                      <OutlinedInput
                        fullWidth
                        required
                        type="text"
                        name="serviceName"
                        label="Service Name"
                        id="service-detail-name"
                        onBlur={handleBlur}
                        onChange={({ target }: any) => {
                          setFieldValue("serviceName", target.value.trimStart());
                        }}
                        value={
                          isEditMode
                            ? values.serviceName
                            : displayOrNA(values.serviceName)
                        }
                        placeholder="Enter service name"
                        inputProps={{ maxLength: 150 }}
                        sx={styles.selectDropdown}
                        disabled={!isEditMode}
                      />
                      {touched.serviceName && errors.serviceName && (
                        <FormHelperText error>
                          {String(errors.serviceName)}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Box>
                </Box>
              </FormSection>

              <FormSection
                index={2}
                title="How it works"
                hint="Break the service into clear, ordered steps."
                count={howItWorksCount}
                countLabel={howItWorksCount === 1 ? "step" : "steps"}
              >
                <TitleDescriptionListInput
                  label=""
                  addButtonLabel="Add step"
                  titlePlaceholder="e.g. High Pressure Wash"
                  descriptionPlaceholder="Describe what happens in this step"
                  emptyTitle="No steps added"
                  emptyHint="Add the first step customers should understand about this service."
                  value={values.howItWorks}
                  error={
                    touched.howItWorks && typeof errors.howItWorks === "string"
                      ? errors.howItWorks
                      : undefined
                  }
                  onChange={(next) => {
                    setFieldValue("howItWorks", next);
                  }}
                  disabled={!isEditMode}
                />
              </FormSection>

              <FormSection
                index={3}
                title="Benefits"
                hint="Highlight why customers should choose this service."
                count={benefitsCount}
                countLabel={benefitsCount === 1 ? "benefit" : "benefits"}
              >
                <TitleDescriptionListInput
                  label=""
                  addButtonLabel="Add benefit"
                  titlePlaceholder="e.g. Quick Cleaning"
                  descriptionPlaceholder="Describe this benefit"
                  emptyTitle="No benefits added"
                  emptyHint="Add the first benefit to showcase on the service page."
                  value={values.benefits}
                  error={
                    touched.benefits && typeof errors.benefits === "string"
                      ? errors.benefits
                      : undefined
                  }
                  onChange={(next) => {
                    setFieldValue("benefits", next);
                  }}
                  disabled={!isEditMode}
                />
              </FormSection>

              <FormSection
                index={4}
                title="Tools"
                hint="List tools used for this service with a photo and name."
                count={toolsCount}
                countLabel={toolsCount === 1 ? "tool" : "tools"}
              >
                <ToolListInput
                  hideHeader
                  label="Tools"
                  placeholder="e.g. Pressure Washer"
                  addButtonLabel="tool"
                  emptyTitle="No tools added"
                  emptyHint="Upload a tool image, enter its name, then click Add tool."
                  value={values.tools}
                  error={touched.tools ? (errors.tools as string) : undefined}
                  onChange={(next) => {
                    toolsFilesRef.current = rememberMediaFiles(
                      next,
                      toolsFilesRef.current
                    );
                    setFieldValue("tools", next);
                  }}
                  onUploadImage={(file, callback) => uploadImageFile(file, callback)}
                  disabled={!isEditMode}
                />
              </FormSection>

              <FormSection
                index={5}
                title="Requirement from user"
                hint="Tell customers what they need to keep ready."
                count={requirementsCount}
                countLabel={
                  requirementsCount === 1 ? "requirement" : "requirements"
                }
              >
                <ToolListInput
                  hideHeader
                  label="Requirement From User"
                  placeholder="e.g. Bucket & water"
                  addButtonLabel="requirement"
                  emptyTitle="No requirements added"
                  emptyHint="Upload an image, enter a requirement title, then click Add requirement."
                  value={values.requirementFromUser}
                  error={
                    touched.requirementFromUser
                      ? (errors.requirementFromUser as string)
                      : undefined
                  }
                  onChange={(next) => {
                    requirementFilesRef.current = rememberMediaFiles(
                      next,
                      requirementFilesRef.current
                    );
                    setFieldValue("requirementFromUser", next);
                  }}
                  onUploadImage={(file, callback) => uploadImageFile(file, callback)}
                  disabled={!isEditMode}
                />
              </FormSection>

              <Box sx={styles.actions}>
                {isEditMode ? (
                  <Button
                    size="large"
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      titleFileRef.current = null;
                      titleImageKeyRef.current = initialValues.image;
                      toolsFilesRef.current = [];
                      requirementFilesRef.current = [];
                      setImageUrl((current) => {
                        if (isLocalPreviewUrl(current)) URL.revokeObjectURL(current);
                        return loadedCoverPreviewRef.current || initialValues.image;
                      });
                      resetForm();
                      exitEditMode();
                    }}
                    sx={styles.cancelBtn}
                  >
                    Discard
                  </Button>
                ) : null}
                {isEditMode ? (
                  <AnimateButton>
                    <Button
                      disableElevation
                      size="large"
                      type="submit"
                      variant="contained"
                      color="secondary"
                      sx={styles.submitBtn}
                      disabled={!dirty && !hasPendingMedia}
                    >
                      Save
                    </Button>
                  </AnimateButton>
                ) : (
                  <AnimateButton>
                    <Button
                      disableElevation
                      size="large"
                      type="button"
                      variant="contained"
                      color="secondary"
                      sx={styles.submitBtn}
                      onClick={() => {
                        setIsEditMode(true);
                        const nextParams = new URLSearchParams(location.search);
                        nextParams.set("edit", "true");
                        navigate(
                          `${ROUTE_NAMES.MAIN_ROUTES.SERVICE_DETAIL}?${nextParams.toString()}`,
                          { replace: true }
                        );
                      }}
                    >
                      Edit
                    </Button>
                  </AnimateButton>
                )}
              </Box>
            </form>
          );
        }}
      </Formik>
      {loading && <Loader />}
    </Box>
  );
};

export default ServiceDetailsPage;
